import { useCallback } from 'react';
import { useAxios } from '../context/AxiosContext';
import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { ClientRepsResponse, UserMutationData } from '../@types/Users';

type SortingOptions = 'ASC' | 'DESC';

export interface ClientRepsListParams {
  search?: string;
  limit?: number;
  sorting?: SortingOptions;
  inactive?: boolean;
}

export function useClientRepsList({
  search = undefined,
  limit = 10,
  sorting = 'ASC',
  inactive = true
}: ClientRepsListParams) {
  const queryCache = useQueryClient();
  const { authAxios } = useAxios();

  const fetchClientReps = useCallback(
    async ({ search, limit, sorting, inactive, pageParam }) => {
      const response = await authAxios.get<ClientRepsResponse>('/users/client-reps', {
        params: {
          search,
          limit,
          inactive,
          mode: sorting,
          endCursor: pageParam
        }
      });
      return response.data;
    },
    [authAxios]
  );

  const updateUsersMutation = useMutation(
    (data: UserMutationData[]) => authAxios.put('/users/client-reps', { users: data }),
    {
      onSuccess: () => {
        queryCache.invalidateQueries('client-reps');
        toast.success('Successfully updated user(s)', { position: 'top-center' });
      },
      onError: () => {
        toast.error('Failed to updated the user(s)', { position: 'top-center' });
      }
    }
  );

  const createUsersMutation = useMutation(
    (data: UserMutationData[]) => authAxios.post('/client-reps', { users: data }),
    {
      onSuccess: () => {
        queryCache.invalidateQueries('client-reps');
        toast.success('The user has been saved successfully', { position: 'top-center' });
      },
      onError: () => {
        toast.error('Failed to save the user.', { position: 'top-center' });
      }
    }
  );

  const reactQuery = useInfiniteQuery(
    ['client-reps', search, limit, sorting, inactive],
    ({ pageParam = undefined }) => fetchClientReps({ search, limit, sorting, inactive, pageParam }),
    {
      suspense: true,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      getNextPageParam: (data: ClientRepsResponse) => data?.pageInfo.hasNextPage && data?.pageInfo.endCursor,
      getPreviousPageParam: (data: ClientRepsResponse) => data?.pageInfo.hasPreviousPage && data?.pageInfo.startCursor
    }
  );

  return {
    ...reactQuery,
    updateUsers: updateUsersMutation.mutateAsync,
    createUsers: createUsersMutation.mutateAsync
  };
}
